import VueRouter from 'vue-router'
// Primary models
import { Assets, Asset } from './observer/assets'
import { Issues, Issue } from './observer/issues'
import { Interventions } from './observer/interventions'
// Secondary models
import { AssetTypes, AssetType } from './observer/asset-types'
import { PlannedIssueDefinitions, PlannedIssueDefinition } from './observer/planned-issue-definitions'
import { IssueCodes, IssueCode } from './observer/issue-codes'
import { Products, Product } from './observer/products'
import { Users, User, UserSettings, UserGroups } from './observer/users'
import { Team } from './observer/teams'
import { Owners, Owner } from './observer/owners'
import { Checklists, ChecklistEditor, ChecklistInIntervention } from './observer/checklists'
// Visual
import { Visual, Visuals } from './observer/visuals'
// Pipe
import { Projects, Project } from './observer/projects'
import { PipeTasks } from './observer/pipe-tasks'
// Reports and Imports
import { Reports, ReportCommonFutureWorkload, ComplianceReport, IssuedCommandsReport, DeviceStatisticsReport, ReportPrg } from './observer/reports'
import { Imports, EntityExportImport } from './observer/imports'
// Logs
import Logs from './observer/history/logs'
// Auth
import LoginView from './observer/sessions/login'
import ResetMyPassword from './observer/sessions/reset-my-password'

import EntityNotFound from './observer/shared/entity-not-found'

import { saveJwtToken, initUserInSession } from './observer/_initiators/login.es6'
import i18n from './i18n'

import { MapView } from './observer/map'
import UsersService from "@/services/users.service.es6";

const router = new VueRouter({
  mode: 'history'
})

const routes = [
  {
    path: '/',
    name: 'home',
    meta: {
      title: i18n.t('app-name')
    },
    beforeEnter: (from, to, next) => {
      const home = UsersService.getHomePage()
      next(home || undefined)
    },
    component: MapView
  },

  // New Routes BEGIN here
  {
    path: '/login',
    name: 'login',
    meta: {
      viewName: 'login-view',
      title: i18n.t('login')
    },
    component: LoginView
  },

  // After clicking on password resetting link (from email)
  {
    path: '/auth-via-token',
    name: 'reset-my-password',
    meta: {
      title: i18n.t('login')
    },
    component: ResetMyPassword
  },

  // Primary models
  {
    path: '/assets',
    name: 'assets',
    meta: {
      title: i18n.tc('assets.entity-name', 2)
    },
    component: Assets
  },
  {
    path: '/assets/:id',
    name: 'assets/id',
    meta: {
      viewName: 'asset',
      title: i18n.tc('assets.entity-name', 1)
    },
    component: Asset
  },
  {
    path: '/issues',
    name: 'issues',
    meta: {
      viewName: 'issues',
      title: i18n.tc('issues.entity-name', 2)
    },
    component: Issues
  },
  {
    path: '/issues/:id',
    name: 'issues/id',
    meta: {
      viewName: 'issue',
      title: i18n.tc('issues.entity-name', 1)
    },
    component: Issue
  },
  {
    path: '/issues/new',
    name: 'issues/new',
    meta: {
      viewName: 'issue',
      title: i18n.t('issues.new')
    },
    component: Issue
  },

  {
    path: '/interventions',
    name: 'interventions',
    meta: {
      viewName: 'interventions',
      title: i18n.tc('interventions.entity-name', 2)
    },
    component: Interventions
  },
  // This route has been replaced by 'issue' with params: {interventionId: id}
  // {
  //   path: '/interventions/:id'
  // },

  // Secondary models

  {
    path: '/asset-types',
    name: 'asset-types',
    meta: {
      viewName: 'asset-types',
      title: i18n.tc('asset-types.entity-name', 2)
    },
    component: AssetTypes
  },
  {
    path: '/asset-types/:id',
    name: 'asset-types/id',
    meta: {
      title: i18n.tc('asset-types.entity-name', 1)
    },
    component: AssetType
  },
  {
    path: '/asset-types/new',
    name: 'asset-types/new',
    meta: {
      title: i18n.t('asset-types.new')
    },
    component: AssetType
  },

  {
    path: '/planned-issue-definitions',
    name: 'planned-issue-definitions',
    meta: {
      viewName: 'planned-issue-definitions',
      title: i18n.tc('planned-issues.entity-name', 2)
    },
    component: PlannedIssueDefinitions
  },
  {
    path: '/planned-issue-definitions/:id',
    name: 'planned-issue-definitions/id',
    meta: {
      title: i18n.tc('planned-issues.entity-name', 1)
    },
    component: PlannedIssueDefinition
  },
  {
    path: '/planned-issue-definitions/new',
    name: 'planned-issue-definitions/new',
    meta: {
      title: i18n.t('planned-issues.new')
    },
    component: PlannedIssueDefinition
  },

  {
    path: '/issue-codes',
    name: 'issue-codes',
    meta: {
      viewName: 'issue-codes',
      title: i18n.tc('issue-codes.entity-name', 2)
    },
    component: IssueCodes
  },
  {
    path: '/issue-codes/:id',
    name: 'issue-codes/id',
    meta: {
      title: i18n.tc('issue-codes.entity-name', 1)
    },
    component: IssueCode
  },
  {
    path: '/issue-codes/new',
    name: 'issue-codes/new',
    meta: {
      title: i18n.t('issue-codes.new')
    },
    component: IssueCode
  },

  {
    path: '/products',
    name: 'products',
    meta: {
      viewName: 'products',
      title: i18n.tc('products.entity-name', 2)
    },
    component: Products
  },
  {
    path: '/:entity/export-import',
    name: 'entity/export-import',
    meta: {
      viewName: 'entity_import-export',
      title: i18n.t('export-import')
    },
    component: EntityExportImport
  },
  {
    path: '/products/:id',
    name: 'products/id',
    meta: {
      title: i18n.tc('products.entity-name', 1)
    },
    component: Product
  },
  {
    path: '/products/new',
    name: 'products/new',
    meta: {
      title: i18n.t('products.new')
    },
    component: Product
  },

  {
    path: '/users',
    name: 'users',
    meta: {
      viewName: 'users',
      title: i18n.tc('users.entity-name', 2)
    },
    component: Users
  },
  {
    path: '/users/:id',
    name: 'users/id',
    meta: {
      title: i18n.tc('users.entity-name', 1)
    },
    component: User
  },
  {
    path: '/users/new',
    name: 'users/new',
    meta: {
      title: i18n.t('users.new')
    },
    component: User
  },
  {
    path: '/user-settings',
    name: 'user-settings',
    meta: {
      title: i18n.t('my-settings')
    },
    component: UserSettings
  },
  {
    path: '/user-groups',
    name: 'user-groups',
    meta: {
      title: i18n.t('user-groups')
    },
    component: UserGroups
  },

  {
    path: '/teams/:id',
    name: 'teams/id',
    meta: {
      title: i18n.t('team'),
      grant: 'worker'
    },
    component: Team
  },

  {
    path: '/owners',
    name: 'owners',
    meta: {
      viewName: 'owners',
      title: i18n.tc('owners.entity-name', 2)
    },
    component: Owners
  },
  {
    path: '/owners/:id',
    name: 'owners/id',
    meta: {
      title: i18n.tc('owners.entity-name', 1)
    },
    component: Owner
  },
  {
    path: '/owners/new',
    name: 'owners/new',
    meta: {
      title: i18n.t('owners.new')
    },
    component: Owner
  },

  {
    path: '/checklists',
    name: 'checklists',
    meta: {
      viewName: 'checklists',
      title: i18n.tc('checklists.entity-name', 2)
    },
    component: Checklists
  },
  {
    path: '/checklists/:id',
    name: 'checklists/id',
    meta: {
      title: i18n.tc('checklists.entity-name', 1)
    },
    component: ChecklistEditor
  },
  {
    path: '/checklists/new',
    name: 'checklists/new',
    meta: {
      title: i18n.t('checklists.new')
    },
    component: ChecklistEditor
  },
  {
    path: '/checklists-in-interventions/:id',
    name: 'checklists-in-interventions/id',
    meta: {
      title: i18n.tc('checklists.entity-name', 1)
    },
    component: ChecklistInIntervention
  },

  {
    path: '/visuals',
    name: 'visuals',
    meta: {
      viewName: 'visuals',
      title: i18n.tc('visuals.entity-name', 2)
    },
    component: Visuals
  },
  {
    path: '/visuals/:id',
    name: 'visuals/id',
    meta: {
      title: i18n.tc('visuals.entity-name', 1)
    },
    component: Visual
  },

  {
    path: '/projects',
    name: 'projects',
    meta: {
      viewName: 'projects',
      title: i18n.tc('projects.entity-name', 2)
    },
    component: Projects
  },
  {
    path: '/projects/:id',
    name: 'projects/id',
    meta: {
      title: i18n.tc('projects.entity-name', 1)
    },
    component: Project
  },
  {
    path: '/projects/new',
    name: 'projects/new',
    meta: {
      title: i18n.t('projects.new')
    },
    component: Project
  },

  {
    path: '/pipe-tasks',
    name: 'pipe-tasks',
    meta: {
      viewName: 'pipe-tasks',
      title: i18n.t('today')
    },
    component: PipeTasks
  },

  {
    path: '/reports',
    name: 'reports',
    meta: {
      viewName: 'reports',
      title: i18n.tc('reports.entity-name', 2)
    },
    component: Reports
  },
  {
    path: '/reports/prg',
    name: 'reports/prg',
    meta: {
      title: i18n.tc('reports.entity-name', 1)
    },
    component: ReportPrg
  },
  {
    path: '/reports/common-future-workload',
    name: 'reports/common-future-workload',
    meta: {
      title: i18n.tc('reports.entity-name', 1)
    },
    component: ReportCommonFutureWorkload
  },
  {
    path: '/reports/compliance-report',
    name: 'reports/compliance-report',
    meta: {
      title: i18n.t('compliance-report')
    },
    component: ComplianceReport
  },
  {
    path: '/reports/issued-commands',
    name: 'reports/issued-commands',
    meta: {
      title: i18n.t('issued-commands')
    },
    component: IssuedCommandsReport
  },
  {
    path: '/reports/device-statistics',
    name: 'reports/device-statistics',
    meta: {
      title: i18n.t('device-statistics')
    },
    component: DeviceStatisticsReport
  },

  {
    path: '/imports',
    name: 'imports',
    meta: {
      viewName: 'imports',
      title: i18n.t('imports')
    },
    component: Imports
  },

  {
    path: '/logs',
    name: 'logs',
    meta: {
      viewName: 'logs',
      title: i18n.t('logs')
    },
    component: Logs
  },

  {
    path: '/city-map',
    name: 'city-map',
    meta: {
      viewName: 'city-map',
      title: i18n.t('city-map')
    },
    component: MapView
  },

  {
    path: '/reset-password',
    name: 'reset/password',
    meta: {
      title: i18n.t('reset-my-password')
    },
    component: UserSettings,
    beforeEnter: async (to, from, next) => {
      // Getting the temporary token from the URL (after the '=' symbol)
      const loginToken = window.location.href.split('=').pop()
      const vm = router.app
      // Auth on the BE using the temporary token, receiving a new auth_token
      try {
        const response = await vm.$http.get('/auth-via-token', { params: { token: loginToken } })
        saveJwtToken(vm, response.data)
        await initUserInSession(vm)
        router.push({ name: 'user-settings' })
      } catch (error) {
        console.error(error)
      }
      next()
    }
  },
  {
    path: '/:entity/:id/not-found',
    name: 'entity-not-found',
    meta: {
      title: i18n.t('entity-not-found')
    },
    component: EntityNotFound
  }
]

router.addRoutes(routes)

router.beforeEach((to, from, next) => {
  let vm = router.app

  // Empty items-space in store (so that no stale data get served)
  vm.$store.commit('emptyItemsSpace')

  vm.$emit('tutorial-scenario', 'default')

  // Prolong the cookie validity
  // TODO (now all sessions are remembered - cookies have unlimited lifetime)

  if (!vm.$cookies.isKey('session') && to.name !== 'login' && to.name !== 'reset/password') {
    router.push({ name: 'login' })
  }

  vm.$store.commit('setLastPage', from.meta.viewName)

  document.title = 'Observer | ' + to.meta.title

  if (window.localStorage.getItem('languageChanged') === 'true') {
    window.localStorage.setItem('languageChanged', 'false')
    router.go()
  }

  next()
})

export default router
